import { Module, Svg } from "@/types";
import { TooltipProps } from "@mui/material";
import {
  GuardPermissionType,
  PermissionGuardExternalProps,
} from "../Guard/PermissionGuard";
import { CustomSearchProps } from "../Search/CustomSearch";
import { SortKey } from "./sorting";

export interface ITableButton {
  showButton?: boolean;
  buttonName?: string;
  ButtonIcon?: any;
  buttonFunction?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
  buttonDisabled?: boolean;
  buttonLoading?: boolean;
  secondaryButtonName?: string;
  ButtonIconSecondary?: any;
  secondaryButtonFunction?: () => void;
  tertiaryButtonName?: string;
  TertiaryButtonIcon?: any;
  tertiaryButtonFunction?: () => void;
  tertiaryButtonDisabled?: boolean;
  tertiaryButtonLoading?: boolean;
  deleteButtonName?: string;
  deleteButtonFunction?: any;
  isLoadingDelete?: boolean;
  addButtonName?: string;
  addButtonFunction?: any;
  isLoadingAdd?: boolean;
  buttonVariant?: "text" | "custom1" | "outlined" | "contained" | undefined;
  primaryButtonPermission?: PermissionGuardExternalProps;
}
export type SearchField = CustomSearchProps;
/**
 * What makes an action column non-empty.
 *
 * Resolution is a full cross-product OR — the column is visible when the user
 * holds ANY of `actions` on ANY of `module`, plus the same `isAdmin` bypass
 * `PermissionGuard` applies:
 *
 *     isAdmin || module.some(m => actions.some(a => checkPermission(...)))
 *
 * It is never AND. Declare the UNION of every button the row can render: a list
 * whose Edit is gated on `update` and whose Delete is gated on `delete` declares
 * both, so a user holding only one of them still gets their column. Reading this
 * as AND would hide the column from that user while the row still had a working
 * button for them, which is the one failure this mechanism exists to prevent.
 *
 * Over-declaring is safe — it costs an occasional column that is present but
 * empty, exactly what shipped before. Under-declaring deletes a usable button.
 */
export interface ActionColumnPermission {
  module?: Module | Module[];
  actions?: GuardPermissionType[];
}

/**
 * The trailing action column, shared so its id stays in step with the regex
 * CustomTable uses to find the column it pins, and so the default width is
 * changed in one place rather than fifteen.
 *
 * `width` defaults to the icon-button case; pass a wider one for tables whose
 * actions are text buttons.
 *
 * Pass `module`/`actions` to have CustomTable drop the column — header AND cell
 * together — for users who can't use any of the buttons in it. Omit them and the
 * column is always visible, which is the right call for lists holding a button
 * that no `PermissionGuard` covers.
 */
export const actionColumn = ({
  width = 160,
  module,
  actions,
}: {
  width?: string | number;
} & ActionColumnPermission = {}): ITableHeaders => ({
  id: "action",
  label: "Actions",
  noSort: true,
  width,
  ...(module && actions ? { permission: { module, actions } } : {}),
});

export interface ITableHeaders {
  id: string;
  label: string;
  colSpan?: number;
  align?: string;
  /**
   * Start this column hidden, leaving it for the user to enable in the picker.
   *
   * For columns that are genuinely useful but not worth the width by default —
   * context you occasionally want to confirm rather than read on every row.
   *
   * Only a SEED, and only for the very first render of a table the user has
   * never touched. The moment they change anything in the picker their choice is
   * stored whole and takes over completely, including re-hiding a column this
   * marks — otherwise a default could keep reasserting itself over the user.
   *
   * Requires the picker (`tableId` + `columnPicker`). Without it there is no way
   * to reveal the column again, so `CustomTable` ignores this flag rather than
   * hiding a column permanently.
   */
  defaultHidden?: boolean;
  /**
   * Hide this column's sort control. It does NOT stop the table sorting by the
   * column — a list may still seed one through `sortByIndex` / `sortBy`, which
   * `AudienceManager/List` does with Created At.
   *
   * Before reaching for this, check whether the column actually needs `sortKey`
   * instead. Most of the `noSort` in this repo predates it and was covering for
   * a sort that read the wrong field and silently did nothing.
   */
  noSort?: boolean;
  /**
   * Where this column's sortable value lives, when that isn't `row[id]`.
   *
   * Sorting resolves `row[sortKey ?? id]`, so a column whose id already names
   * its field needs nothing here. Everything else does: a renamed field
   * (`archived` → `active`), a nested one (`"Advertiser.name"`), or a value the
   * cell computes rather than reads (a coverage ratio, a name resolved through
   * a lookup) — pass a function for those.
   *
   * Return the value to ORDER BY, not the string the cell shows. See `SortKey`
   * in `./sorting`.
   */
  sortKey?: SortKey;
  /**
   * Explicit column width. `CustomTable` switches to `table-layout: fixed` only
   * when EVERY header declares one, so widths are honoured exactly instead of
   * being treated as hints. Px values are exact; percentages are relative to the
   * table width.
   *
   * All or nothing: if any header in the array omits `width`, the declared ones
   * have NO effect whatsoever — not a weaker hint, nothing. They are emitted as
   * a single generated rule set that is skipped entirely unless the array is
   * uniform. Every header array in the repo is currently all-width or
   * zero-width; adding a width to one column of a zero-width table will appear
   * to do nothing, and that is why.
   */
  width?: string | number;
  /**
   * Opt this column out of single-line ellipsis truncation. Use for cells that
   * render chips, icons, buttons or stacked content, where `overflow: hidden`
   * would clip rather than ellipsise.
   */
  noTruncate?: boolean;
  /**
   * What this column means, shown on hovering the header itself after a short
   * delay — there is no ⓘ beside it any more, so nothing steals width from the
   * label in a narrow column. Only rendered on tables that pass `isToolTip`.
   */
  toolTip?: React.ReactNode;
  /**
   * EXPERIMENTAL — under `cellDisplay="wrap"`, only columns marked here wrap;
   * everything else keeps one line and an ellipsis.
   *
   * Wrapping is per-column because it is only ever right for prose-ish columns.
   * A 60-character name in a 220px column wraps to three tidy lines; the same
   * rule on an 80px OSI column renders "Under-" / "deliveri" / "ng", because
   * breaking mid-word is the only way to fit. Mark the long text columns —
   * names, campaigns, advertisers — and leave dates, money and status alone.
   */
  wrap?: boolean;
  /**
   * The column that absorbs leftover width when the table is wider than its
   * declared columns add up to. At most one per table; `width` then acts as its
   * minimum, since the table never shrinks below the declared total.
   *
   * Without this the slack goes to whichever column happens to be last, because
   * that is what Chrome's fixed-layout algorithm does with the remainder — which
   * meant the action column rendering at 300px for a declared 160px, with its
   * buttons stranded at the left of a cell nearly twice the size. Put it on the
   * name column: extra width there is extra room to read, and one fewer wrapped
   * line.
   */
  grow?: boolean;
  /**
   * This column's cells can be edited when the table is in edit mode.
   *
   * Declares the fact in the same place as the rest of the column, and it is
   * what `layoutSx` branches on to stand the line clamp down for the cell —
   * which is why it is part of `headerKey`. The behaviour itself comes from the
   * table's `editing` config naming this `id` in its `fields`; marking a header
   * with no matching field does nothing.
   */
  editable?: boolean;
  /**
   * ACTION COLUMN ONLY. Honoured solely on the header whose `id` matches
   * `/^actions?$/i`; set it anywhere else and it is ignored on purpose.
   *
   * Hiding a header is only safe when the matching `<td>` disappears with it,
   * and the only cell wired to do that is `<ActionCell />`. A data column that
   * vanished from the head while its cell kept rendering would put every
   * `nth-of-type` rule one column out — the desync that cost a whole PR to
   * remove — so the restriction is enforced in `useVisibleHeaders` rather than
   * left to whoever reads this.
   *
   * See `ActionColumnPermission` for how the fields combine.
   */
  permission?: ActionColumnPermission;
}

export interface ITablePaginaion {
  pagination?: boolean;
  page?: number;
  handleChangePage?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => void;
  rowsPerPage?: number;
  totalCount?: number;
  handleChangeRowsPerPage?: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
}

export type SelectedAction = {
  type: string;
  icon: Svg;
  handleOnClick: (args?: any) => void;
};

/**
 * Overrides for a header's tooltip — placement, arrow, and so on. The title
 * comes from the header's own `toolTip`, and the tooltip now wraps the header
 * label, so there is no icon left to size: `icon` and `iconSize` are gone from
 * this on purpose, and passing one is a type error rather than a prop that
 * quietly does nothing.
 */
export type TableToolTipProps = Omit<TooltipProps, "title" | "children">;

export type TableRowData = Record<string, string | number | string[]>;
